import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Input, Button, UncontrolledTooltip } from "@/design-system/native";
import { RefreshCcw,  HelpCircle, X } from "lucide-react";
import dayjs from "dayjs";
import DynamicModalForm from "../../../../Components/Modal/DynamicModalForm";
import DataTable from "../../../../Components/Tables/DataTable/DataTable";
import RemindersContext from "../../../../_helper/Reminders";
import styles from "./RemindersModal.module.scss";

const repeatOptions = [
  { value: "Day(s)", label: "Day(s)" },
  { value: "Week(s)", label: "Week(s)" },
  { value: "Month(s)", label: "Month(s)" },
];

const initialForm = {
  title: "",
  date: "",
  time: "",
  repeat: false,
  repeatEvery: 1,
  repeatUnit: repeatOptions[0].value,
  cycles: "",
};

const RemindersModal = ({ isOpen, toggle }) => {
  const remindersCtx = useContext(RemindersContext);
  const reminders = remindersCtx?.reminders || [];

  const [formData, setFormData] = useState(initialForm);

  // Modal her açıldığında formu temizle
  useEffect(() => {
    if (isOpen) setFormData(initialForm);
  }, [isOpen]);

  const handleDelete = useCallback(
    (id) => remindersCtx?.deleteReminder?.(id),
    [remindersCtx]
  );

  const handleSave = useCallback(
    (data) => {
      const { title, date, time, repeat, repeatEvery, repeatUnit, cycles } = data;
      if (!title || !date || !time) return;

      remindersCtx?.addReminder?.({
        title,
        date,
        time,
        repeat,
        repeatEvery: repeat ? repeatEvery || 1 : null,
        repeatUnit: repeat ? repeatUnit || repeatOptions[0].value : null,
        cycles: repeat ? cycles || null : null,
      });

      setFormData(initialForm);
    },
    [remindersCtx]
  );

  const formatDateTime = useCallback((item) => {
    if (!item?.date) return "-";
    const date = dayjs(item.date);
    const today = dayjs();
    const prefix = date.isSame(today, "day") ? "Today" : date.format("ddd, MMM DD");
    return `${prefix}, ${item.time || "--:--"}`;
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "title",
        header: "Title",
        accessor: "title",
        cell: (row) => (
          <div className={styles.titleCell}>
            <span className={styles.titleText}>{row.title}</span>
            <span className={styles.subText}>{formatDateTime(row)}</span>
          </div>
        ),
      },
      {
        id: "repeat",
        header: "Repeat",
        accessor: "repeat",
        cell: (row) =>
          row.repeat ? (
            <div className={styles.repeatCell}>
              <RefreshCcw size={14} />
              <span>
                {row.repeatEvery || 1} {row.repeatUnit || repeatOptions[0].value}
                {row.cycles ? ` • ${row.cycles} cycles` : ""}
              </span>
            </div>
          ) : (
            <span className={styles.mutedText}>No</span>
          ),
      },
      {
        id: "actions",
        header: "",
        width: 70,
        cell: (row) => (
          <Button className={styles.deleteBtn} color="light" size="xs" onClick={() => handleDelete(row.id)}>
             <X color="#10161b" size={16} />
          </Button>
        ),
      },
    ],
    [handleDelete, formatDateTime]
  );

  const fields = useMemo(() => {
    const base = [
      {
        type: "text",
        name: "title",
        placeholder: "Title",
        label: "Title",
        labelCol: 3,
        fieldCol: 9,
        xsLabel: 12,
        xsField: 12,
      },
      {
        type: "dateTimeRow",
        label: "Date & Time",
        leftDateName: "date",
        rightTimeName: "time",
        labelCol: 3,
        fieldCol: 9,
        xsLabel: 12,
        xsField: 12,
      },
      {
        type: "checkbox",
        name: "repeat",
        label: "Repeat",
        asRow: false,
        labelCol: 0,
        fieldCol: 12,
        info: "Cron Job is required for this action!",
      },
    ];

    if (formData.repeat) {
      base.push(
        {
          type: "custom",
          asRow: true,
          label: "Repeat every",
          labelCol: 3,
          fieldCol: 9,
          xsLabel: 12,
          xsField: 12,
          render: ({ localForm, setLocalForm, onFormChange }) => (
            <div className={styles.repeatRow}>
              <div className={styles.colEvery}>
                <Input
                  type="number"
                  min={1}
                  name="repeatEvery"
                  value={localForm.repeatEvery ?? 1}
                  onChange={(e) => {
                    const updated = {
                      ...localForm,
                      repeatEvery: Number(e.target.value) || 1,
                    };
                    setLocalForm(updated);
                    onFormChange(updated);
                  }}
                  placeholder="Repeat every"
                  className="app-input app-input--placeholder"
                />
              </div>
              <div className={styles.colUnit}>
                <Input
                  type="select"
                  name="repeatUnit"
                  value={localForm.repeatUnit || repeatOptions[0].value}
                  onChange={(e) => {
                    const updated = { ...localForm, repeatUnit: e.target.value };
                    setLocalForm(updated);
                    onFormChange(updated);
                  }}
                  className="app-input app-input--placeholder"
                >
                  {repeatOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </div>
            </div>
          ),
        },
        {
          type: "custom",
          asRow: true,
          label: "Cycles",
          labelCol: 3,
          fieldCol: 9,
          xsLabel: 12,
          xsField: 12,
          render: ({ localForm, setLocalForm, onFormChange }) => (
            <div className={styles.cyclesRow}>
              <Input
                type="number"
                min={1}
                name="cycles"
                value={localForm.cycles ?? ""}
                onChange={(e) => {
                  const updated = { ...localForm, cycles: e.target.value };
                  setLocalForm(updated);
                  onFormChange(updated);
                }}
                placeholder="Cycles"
                className={`app-input app-input--placeholder ${styles.cyclesInput}`}
              />
              <>
                <HelpCircle
                  id="cycles-info-icon"
                  size={16}
                  className={styles.infoIcon}
                />
                <UncontrolledTooltip placement="right" className={`reminders-tooltip ${styles.tooltipDark}`} innerClassName={styles.tooltipInner} target="cycles-info-icon" trigger="hover">
                  Recurring will be stopped after the number of cycles. Keep it blank for infinity.
                </UncontrolledTooltip>
              </>
            </div>
          ),
        }
      );
    }

    // Liste alanı
    base.push({
      type: "custom",
      label: "",
      render: () => (
        <div className={styles.listWrap}>
          {reminders.length ? (
            <DataTable columns={columns} rows={reminders} />
          ) : (
            <div className={styles.emptyState}>
              No record found.
            </div>
          )}
        </div>
      ),
    });

    return base;
  }, [formData.repeat, reminders, columns]);

  const footerLeft = (
    <Button color="light" className={styles.footerBtn} onClick={() => {}}>
      Show all reminders
    </Button>
  );

  return (
    <DynamicModalForm
      isOpen={isOpen}
      toggle={toggle}
      title="Reminders (Private)"
      fields={fields}
      formData={formData}
      onFormChange={setFormData}
      onSave={handleSave}
      saveButtonText="Add"
      saveButtonColor="primary"
      closeOnSave={false}
      footerLeft={footerLeft}
    />
  );
};

export default RemindersModal;
